import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import {
  Globe, Save, Loader2, ExternalLink, ShieldCheck, Eye, EyeOff, Sparkles,
  Upload, Trash2, Image as ImageIcon, Camera,
} from "lucide-react";
import { QRShareCard } from "../components/QRShareCard";
import { absUrl } from "../lib/seo";

const FIELDS = [
  ["public_business_name",  "Business name (public)",     "Falls back to your company name."],
  ["public_city_area",      "City / area (public)",       "Be specific: e.g. \"Dumaguete City, Negros Oriental\"."],
  ["public_description",    "Description",                "1–3 short paragraphs. What makes you trusted?"],
  ["public_facebook_url",   "Facebook page URL",          "Optional. https://facebook.com/..."],
  ["public_messenger_url",  "Messenger URL",              "Optional. https://m.me/..."],
  ["public_whatsapp",       "WhatsApp number",            "Optional. +63…"],
];

const VIS_TOGGLES = [
  ["show_in_public_directory",    "Show this business on the Discover Rentals directory."],
  ["show_phone_publicly",         "Show my phone number on the public profile."],
  ["show_email_publicly",         "Show my email on the public profile."],
  ["show_rates_publicly",         "Show vehicle daily rates on the public profile."],
  ["show_vehicle_list_publicly",  "Show the vehicle list on the public profile."],
];

export default function PublicProfile() {
  const { user, company } = useAuth();
  const isOwner = (user?.account_type || user?.role) === "business_owner";
  const [form, setForm] = useState(null);
  const [pickups, setPickups] = useState("");
  const [saving, setSaving] = useState(false);
  const [photos, setPhotos] = useState({ logo: null, about: [], max_about: 5 });

  const load = async () => {
    try {
      const { data } = await api.get("/company/public-profile");
      setForm(data);
      setPickups((data.public_pickup_locations || []).join("\n"));
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const loadPhotos = async () => {
    try {
      const { data } = await api.get("/business/photos");
      setPhotos(data);
    } catch (_) { /* tolerate */ }
  };
  useEffect(() => { load(); loadPhotos(); }, []);

  if (!form) {
    return <div className="text-slate-500 py-10 text-center"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Loading…</div>;
  }
  if (!isOwner) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-6 text-amber-900 max-w-2xl" data-testid="pp-no-access">
        Only the business owner can edit the public profile.
      </div>
    );
  }

  const update = (k, v) => setForm({ ...form, [k]: v });
  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, public_pickup_locations: pickups.split("\n").map((s) => s.trim()).filter(Boolean) };
      const { data } = await api.put("/company/public-profile", payload);
      setForm(data);
      toast.success("Public profile updated");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const completeness = form.completeness || { score: 0, missing: [] };
  return (
    <div className="space-y-6 max-w-3xl" data-testid="public-profile-page">
      <div>
        <h1 className="font-display text-3xl font-bold inline-flex items-center gap-2">
          <Globe className="w-6 h-6" /> Public profile
        </h1>
        <p className="text-sm text-slate-600 mt-1">
          What customers see on <Link to="/rentals" target="_blank" rel="noreferrer" className="underline">Discover Rentals</Link> and your public business page.
        </p>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5 flex items-center justify-between gap-4 flex-wrap" data-testid="pp-completeness">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Profile completeness</div>
          <div className="mt-1 flex items-center gap-2">
            <div className="h-2 w-40 bg-slate-200 rounded-full overflow-hidden">
              <div className="h-full bg-emerald-500" style={{ width: `${Math.round(completeness.score * 100)}%` }} />
            </div>
            <span className="text-sm font-mono">{Math.round(completeness.score * 100)}%</span>
          </div>
          {completeness.missing?.length > 0 && (
            <p className="mt-1 text-xs text-slate-500">
              <Sparkles className="w-3 h-3 inline mr-0.5" />
              Add: {completeness.missing.map((m) => m.replace(/_/g, " ").replace("public ", "")).join(", ")}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-xs px-2 py-0.5 rounded-full border font-medium inline-flex items-center gap-1 ${
            form.show_in_public_directory
              ? "bg-emerald-100 text-emerald-800 border-emerald-200"
              : "bg-slate-100 text-slate-700 border-slate-200"
          }`} data-testid="pp-visibility-badge">
            {form.show_in_public_directory ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
            {form.show_in_public_directory ? "Visible in directory" : "Hidden from directory"}
          </span>
          {form.show_in_public_directory && (
            <a href="/rentals" target="_blank" rel="noreferrer" data-testid="pp-preview"
                className="text-xs px-2 py-1 rounded-md border border-slate-300 hover:bg-slate-50 inline-flex items-center gap-1">
              <ExternalLink className="w-3 h-3" /> Preview directory
            </a>
          )}
        </div>
      </div>

      <PublicProfileShareCard
        company={company}
        directoryVisible={!!form.show_in_public_directory}
      />

      <PhotosSection
        photos={photos}
        onChange={loadPhotos}
      />

      <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-3">
        <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Visibility</div>
        {VIS_TOGGLES.map(([key, label]) => (
          <label key={key} className="flex items-start gap-2 text-sm">
            <input type="checkbox" checked={!!form[key]} onChange={(e) => update(key, e.target.checked)}
                    data-testid={`pp-toggle-${key}`} className="mt-1" />
            <span>{label}</span>
          </label>
        ))}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-4">
        <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Public copy</div>
        {FIELDS.map(([key, label, hint]) => (
          <div key={key} className="space-y-1">
            <label className="text-xs font-medium text-slate-700">{label}</label>
            {key === "public_description" ? (
              <textarea
                rows={4}
                value={form[key] || ""}
                onChange={(e) => update(key, e.target.value)}
                data-testid={`pp-${key}`}
                className="w-full px-3 py-2 rounded-lg border border-slate-300 bg-white focus:border-[#1B4332] focus:ring-2 focus:ring-[#1B4332]/15 outline-none text-sm"
              />
            ) : (
              <input
                type="text"
                value={form[key] || ""}
                onChange={(e) => update(key, e.target.value)}
                data-testid={`pp-${key}`}
                className="w-full px-3 py-2 rounded-lg border border-slate-300 bg-white focus:border-[#1B4332] focus:ring-2 focus:ring-[#1B4332]/15 outline-none text-sm"
              />
            )}
            <p className="text-[11px] text-slate-500">{hint}</p>
          </div>
        ))}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-3">
        <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Pickup locations</div>
        <p className="text-xs text-slate-500">One per line.</p>
        <textarea rows={4} value={pickups} onChange={(e) => setPickups(e.target.value)}
                   data-testid="pp-pickup-locations"
                   className="w-full px-3 py-2 rounded-lg border border-slate-300 bg-white outline-none text-sm font-mono" />
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-2">
        <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Public terms</div>
        <textarea rows={5} value={form.public_terms || ""} onChange={(e) => update("public_terms", e.target.value)}
                   data-testid="pp-public-terms"
                   className="w-full px-3 py-2 rounded-lg border border-slate-300 bg-white outline-none text-sm" />
        <p className="text-[11px] text-slate-500">Driver age, ID, fuel, smoking, pets: anything customers should know before booking.</p>
      </div>

      <div className="flex items-center justify-end gap-2">
        <button onClick={save} disabled={saving} data-testid="pp-save"
                className="px-4 py-2 rounded-lg btn-brand font-medium text-sm inline-flex items-center gap-1 disabled:opacity-60">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          Save public profile
        </button>
      </div>

      <p className="text-xs text-slate-500 inline-flex items-center gap-1">
        <ShieldCheck className="w-3 h-3" />
        Internal notes, financial records, and staff details are never exposed on the public profile.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Logo + About-page images (max 5)
// ---------------------------------------------------------------------------

async function fileToBase64(file) {
  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      const idx = String(result).indexOf("base64,");
      resolve(idx >= 0 ? String(result).slice(idx + "base64,".length) : "");
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function PhotosSection({ photos, onChange }) {
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingAbout, setUploadingAbout] = useState(false);

  const handleLogo = async (file) => {
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) { toast.error("Logo too large: max 4MB"); return; }
    setUploadingLogo(true);
    try {
      const data_base64 = await fileToBase64(file);
      await api.post("/business/photos/logo", { name: file.name, content_type: file.type, data_base64 });
      toast.success("Logo updated");
      await onChange();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploadingLogo(false); }
  };

  const removeLogo = async () => {
    if (!window.confirm("Remove your business logo?")) return;
    try {
      await api.delete("/business/photos/logo");
      toast.success("Logo removed");
      await onChange();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const handleAbout = async (file) => {
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) { toast.error("Image too large: max 4MB"); return; }
    if ((photos.about?.length || 0) >= (photos.max_about || 5)) {
      toast.error(`You can publish at most ${photos.max_about} about-page images.`);
      return;
    }
    setUploadingAbout(true);
    try {
      const data_base64 = await fileToBase64(file);
      await api.post("/business/photos", { name: file.name, content_type: file.type, data_base64 });
      toast.success("Photo added");
      await onChange();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploadingAbout(false); }
  };

  const removeAbout = async (id) => {
    if (!window.confirm("Remove this photo?")) return;
    try {
      await api.delete(`/business/photos/${id}`);
      toast.success("Photo removed");
      await onChange();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-5" data-testid="pp-photos-section">
      <div>
        <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Logo</div>
        <p className="text-xs text-slate-500 mt-0.5">Square works best. Shown on Discover Rentals cards + your public profile.</p>
        <div className="mt-3 flex items-center gap-4">
          <div className="w-20 h-20 rounded-xl border border-slate-200 bg-slate-50 grid place-items-center overflow-hidden flex-shrink-0">
            {photos.logo ? (
              <LogoPreview logo={photos.logo} />
            ) : (
              <ImageIcon className="w-7 h-7 text-slate-300" />
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <label className="px-3 py-2 rounded-lg btn-brand text-sm inline-flex items-center gap-1 cursor-pointer" data-testid="pp-logo-upload-label">
              <Upload className="w-3.5 h-3.5" /> {uploadingLogo ? "Uploading…" : (photos.logo ? "Replace logo" : "Upload logo")}
              <input type="file" accept="image/jpeg,image/png,image/webp,image/heic"
                      data-testid="pp-logo-upload-input" disabled={uploadingLogo}
                      onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; handleLogo(f); }}
                      className="hidden" />
            </label>
            {photos.logo && (
              <button type="button" onClick={removeLogo} data-testid="pp-logo-remove"
                      className="px-3 py-2 rounded-lg border border-rose-300 text-rose-700 bg-white hover:bg-rose-50 text-sm inline-flex items-center gap-1">
                <Trash2 className="w-3.5 h-3.5" /> Remove
              </button>
            )}
          </div>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">About-page photos</div>
            <p className="text-xs text-slate-500 mt-0.5">
              {photos.about?.length || 0} / {photos.max_about || 5}: show your office, your cars, your team.
            </p>
          </div>
          <label className={`px-3 py-2 rounded-lg text-sm inline-flex items-center gap-1 cursor-pointer ${
            (photos.about?.length || 0) >= (photos.max_about || 5)
              ? "border border-slate-200 text-slate-400 cursor-not-allowed"
              : "btn-brand"
          }`} data-testid="pp-about-upload-label">
            <Camera className="w-3.5 h-3.5" /> {uploadingAbout ? "Uploading…" : "Add photo"}
            <input type="file" accept="image/jpeg,image/png,image/webp,image/heic"
                    data-testid="pp-about-upload-input" disabled={uploadingAbout || (photos.about?.length || 0) >= (photos.max_about || 5)}
                    onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; handleAbout(f); }}
                    className="hidden" />
          </label>
        </div>
        {(photos.about?.length || 0) === 0 ? (
          <div className="mt-3 text-sm text-slate-500 py-6 text-center border border-dashed border-slate-200 rounded-lg">
            No about-page photos yet. Add up to {photos.max_about || 5} so customers can see what your office and fleet look like.
          </div>
        ) : (
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="pp-about-grid">
            {photos.about.map((img) => (
              <AboutPhotoCard key={img.id} img={img} onRemove={() => removeAbout(img.id)} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function LogoPreview({ logo }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get(`/business/photos/data/${logo.id}`)
      .then((r) => { if (alive) setSrc(r.data.data_url); })
      .catch(() => {});
    return () => { alive = false; };
  }, [logo.id]);
  if (!src) return <Loader2 className="w-5 h-5 animate-spin text-slate-300" />;
  return <img src={src} alt="Business logo" className="w-full h-full object-cover" data-testid="pp-logo-preview" />;
}

function AboutPhotoCard({ img, onRemove }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get(`/business/photos/data/${img.id}`)
      .then((r) => { if (alive) setSrc(r.data.data_url); })
      .catch(() => {});
    return () => { alive = false; };
  }, [img.id]);
  return (
    <div className="relative rounded-lg overflow-hidden border border-slate-200 bg-slate-50 group" data-testid={`pp-about-${img.id}`}>
      <div className="aspect-video w-full bg-slate-100 grid place-items-center">
        {src ? <img src={src} alt={img.name} className="w-full h-full object-cover" /> : <Loader2 className="w-5 h-5 animate-spin text-slate-300" />}
      </div>
      <div className="p-2 text-xs truncate" title={img.name}>{img.name}</div>
      <button
        type="button" onClick={onRemove} data-testid={`pp-about-remove-${img.id}`}
        className="absolute top-1 right-1 p-1 rounded-md bg-white/90 text-rose-700 opacity-0 group-hover:opacity-100 transition"
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Share-by-QR card: surfaces the customer-facing canonical URL so owners can
// print a QR, screenshot it for chat, or paste the link into Facebook/Viber.
// We always link to the public business page; if the business has unlocked
// the Enterprise mini-site and set a slug, we also surface that nicer URL.
// ---------------------------------------------------------------------------
function PublicProfileShareCard({ company, directoryVisible }) {
  if (!company?.id) {
    return (
      <div
        data-testid="pp-share-pending"
        className="rounded-xl border border-slate-200 bg-slate-50/60 p-5 text-sm text-slate-500"
      >
        Save your business details to create the shareable QR code.
      </div>
    );
  }

  const businessUrl = absUrl(`/business/${company.id}`);
  const slug = company.public_slug;
  const miniSiteEnabled = !!company.public_website_enabled && !!slug;
  const miniSiteUrl = miniSiteEnabled ? absUrl(`/rentals/${slug}/home`) : null;

  // When the mini-site is live, that is the canonical "Share me" link.
  const primaryUrl = miniSiteUrl || businessUrl;
  const primaryLabel = miniSiteEnabled
    ? "Share your booking website"
    : "Share your public business page";
  const primaryDescription = miniSiteEnabled
    ? "Scan to open your full mini-site. Perfect for tarpaulins, flyers, or printed price lists."
    : "Print this on your shop window or share it on chat. Customers scan to view your rentals.";
  const primaryFilename = miniSiteEnabled ? `confirmph-${slug}` : `confirmph-business-${company.id.slice(0, 8)}`;

  return (
    <div className="space-y-4" data-testid="pp-share-section">
      {!directoryVisible && (
        <div
          className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
          data-testid="pp-share-hidden-warning"
        >
          Your public profile is currently hidden from the Discover Rentals directory.
          Customers will still reach the page through a direct link or this QR code.
        </div>
      )}
      <QRShareCard
        url={primaryUrl}
        label={primaryLabel}
        description={primaryDescription}
        filename={primaryFilename}
        testIdPrefix="pp-share-primary"
      />
      {miniSiteEnabled && (
        <details className="rounded-xl border border-slate-200 bg-white p-4" data-testid="pp-share-fallback-wrap">
          <summary className="cursor-pointer text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
            Also need the classic /business/&#123;id&#125; link?
          </summary>
          <div className="mt-3">
            <QRShareCard
              url={businessUrl}
              label="Fallback business link"
              description="Direct ConfirmPH listing: works even if your mini-site is paused."
              filename={`confirmph-business-${company.id.slice(0, 8)}`}
              testIdPrefix="pp-share-fallback"
            />
          </div>
        </details>
      )}
    </div>
  );
}
